import { ProductDuration } from "../../Models/OrderBookEntry";

export type Grid = {
  deliveryAreaId: number;
  /** Wire identifier: what lazerhub reports as `delivery_area_id`. */
  eicCode: string;
  currencyCode: string;
  /** Wire identifier: the NATS subject token. Same value as `eicCode` for JEPX. */
  areaCode: string;
  /** Human-readable name. Use this for anything rendered or put in a URL. */
  displayName: string;
  timeZone: string;
  countryIsoCode: string;
  ProductDurations: ProductDuration[];
  deleted: boolean;
};

export const toAreaId = (deliveryArea: string): string =>
  GRIDS.find((grid) => grid.displayName === deliveryArea)?.eicCode ??
  deliveryArea;

export const find = (deliveryArea: string): Grid => {
  const eicCode = toAreaId(deliveryArea);
  const grid = GRIDS.find(
    (grid) => grid.eicCode === eicCode && grid.deleted === false
  );
  // Fall back to a stub so algos referencing unknown delivery areas
  // (e.g. stale data on a shared hub) don't crash the grids.
  return (
    grid ?? {
      deliveryAreaId: -1,
      eicCode,
      currencyCode: "JPY",
      areaCode: eicCode,
      displayName: eicCode,
      timeZone: "Asia/Tokyo",
      countryIsoCode: "JP",
      ProductDurations: [],
      deleted: false,
    }
  );
};

// JEPX delivery areas — Japan's 9 service areas.
//
// JEPX has no EIC codes. Its own identifier for a bidding area is the bare digit
// 1-9, and that is exactly what lazer_trader puts in `delivery_area_id` and in the
// NATS subject (`contracts.jepx.3.<epoch_ms>`), so eicCode and areaCode are both
// the digit. `displayName` exists purely for the UI.
//
// Note lazer_trader currently whitelists Tokyo only (`jepx-areas-whitelist`), so
// area 3 is the only one that will carry data today.
// TODO: confirm JEPX product durations (30-min products assumed; H/Q?).
const GRIDS: Grid[] = [
  {
    deliveryAreaId: 1,
    eicCode: "1",
    currencyCode: "JPY",
    areaCode: "1",
    displayName: "Hokkaido",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 2,
    eicCode: "2",
    currencyCode: "JPY",
    areaCode: "2",
    displayName: "Tohoku",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 3,
    eicCode: "3",
    currencyCode: "JPY",
    areaCode: "3",
    displayName: "Tokyo",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 4,
    eicCode: "4",
    currencyCode: "JPY",
    areaCode: "4",
    displayName: "Chubu",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 5,
    eicCode: "5",
    currencyCode: "JPY",
    areaCode: "5",
    displayName: "Hokuriku",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 6,
    eicCode: "6",
    currencyCode: "JPY",
    areaCode: "6",
    displayName: "Kansai",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 7,
    eicCode: "7",
    currencyCode: "JPY",
    areaCode: "7",
    displayName: "Chugoku",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 8,
    eicCode: "8",
    currencyCode: "JPY",
    areaCode: "8",
    displayName: "Shikoku",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
  {
    deliveryAreaId: 9,
    eicCode: "9",
    currencyCode: "JPY",
    areaCode: "9",
    displayName: "Kyushu",
    timeZone: "Asia/Tokyo",
    countryIsoCode: "JP",
    ProductDurations: [ProductDuration.HH],
    deleted: false,
  },
];

export { GRIDS };
